import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import AppLayout from "@/components/app-layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, MessageSquare, Info } from "lucide-react";
import { format } from "date-fns";
import { usePageTitle } from "@/hooks/use-page-title";
import { ConversationChat } from "@/components/conversation-chat";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

interface PartnerThread {
  id: number;
  subject: string | null;
  threadType: string;
  unreadByUser: number;
  partnerName: string | null;
  partnerType: string | null;
  createdAt: string;
  updatedAt: string | null;
  lastMessageAt?: string | null;
}

const COMPLIANCE_NOTICE =
  "Conversations on PadPulse are monitored by PadPulse for quality and compliance.";

function ThreadList({
  threads,
  onSelect,
}: {
  threads: PartnerThread[];
  onSelect: (t: PartnerThread) => void;
}) {
  if (threads.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <MessageSquare className="h-10 w-10 mx-auto mb-3 text-slate-300" />
        <p className="text-sm">No partner conversations yet.</p>
        <p className="text-xs mt-1 text-slate-400">
          Connect with a lender from the Finance tab to start a conversation.
        </p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-slate-100" data-testid="list-partner-threads">
      {threads.map((t) => (
        <li key={t.id}>
          <button
            className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
            onClick={() => onSelect(t)}
            data-testid={`item-partner-thread-${t.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {t.subject || "Partner message"}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {t.partnerName || "Partner"} {t.partnerType ? `· ${t.partnerType}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {t.unreadByUser > 0 && (
                  <Badge className="bg-blue-600 text-white text-xs px-1.5 py-0.5">
                    {t.unreadByUser}
                  </Badge>
                )}
                <span className="text-xs text-slate-400">
                  {format(new Date(t.lastMessageAt || t.updatedAt || t.createdAt), "MMM d")}
                </span>
              </div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ThreadView({
  thread,
  onBack,
}: {
  thread: PartnerThread;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          data-testid="button-back-threads"
          className="shrink-0"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">
            {thread.subject || "Partner message"}
          </p>
          <p className="text-xs text-slate-500">
            {thread.partnerName || "Partner"}
          </p>
        </div>
      </div>

      <div className="flex-1 p-3">
        <ConversationChat
          source="partner"
          conversationId={thread.id}
          counterpartyName={thread.partnerName || "Partner"}
          heightClass="h-[420px]"
        />
      </div>
    </div>
  );
}

export default function AppPartnerConversations() {
  usePageTitle("Partner Conversations");
  const [selectedThread, setSelectedThread] = useState<PartnerThread | null>(null);

  const threadsQuery = useQuery<PartnerThread[]>({
    queryKey: ["/api/app/partner-threads"],
  });

  const threads = threadsQuery.data || [];

  // Auto-select thread from ?thread=X query param (used by email deep-link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const threadId = parseInt(params.get("thread") || "", 10);
    if (!isNaN(threadId) && threads.length > 0 && !selectedThread) {
      const match = threads.find((t) => t.id === threadId);
      if (match) setSelectedThread(match);
    }
  }, [threads]);

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1
              className="text-2xl font-bold text-slate-900"
              data-testid="text-partner-conv-title"
              data-tour="partner-conversations-heading"
            >
              Partner conversations
            </h1>
            <p className="text-slate-500 text-sm">
              Messages with PadPulse partner lenders, wholesalers, and realtors.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="partner-conversations" />
            <GuideButton pageKey="partner-conversations" />
          </div>
        </div>

        <GuideNudgeBanner pageKey="partner-conversations" />

        <Card className="overflow-hidden mt-4" data-tour="partner-conversations-card">
          {selectedThread ? (
            <div style={{ minHeight: 480 }} className="flex flex-col">
              <ThreadView
                thread={selectedThread}
                onBack={() => setSelectedThread(null)}
              />
            </div>
          ) : (
            <CardContent className="p-0">
              {threadsQuery.isLoading ? (
                <div className="p-4 space-y-3">
                  <Skeleton className="h-14 w-full" />
                  <Skeleton className="h-14 w-full" />
                </div>
              ) : (
                <ThreadList threads={threads} onSelect={setSelectedThread} />
              )}
            </CardContent>
          )}
        </Card>

        <p
          className="text-xs text-slate-400 mt-4 flex items-center gap-1.5"
          data-testid="notice-compliance-footer"
          data-tour="partner-conversations-compliance"
        >
          <Info className="h-3.5 w-3.5" />
          {COMPLIANCE_NOTICE}
        </p>
      </div>
    </AppLayout>
  );
}
